import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  type Column,
  EmptyState,
  Kpi,
  PageHead,
  Pill,
  Select,
  Table,
  Tabs,
  type TabItem,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useClients } from "@/hooks/useClients";
import { useContractsPaged } from "@/hooks/useContracts";
import type { Contract, ContractStatus, OperatorType } from "@/types/contract";
import { formatDate, formatPct } from "@/utils/fmt";
import { ContractTemplatesPanel } from "./ContractTemplatesPanel";
import { ContractWizard } from "./ContractWizard";
import "./Contracts.css";

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: "Rascunho",
  active: "Ativo",
  paused: "Pausado",
  finished: "Encerrado",
  canceled: "Cancelado",
};

const STATUS_TONE: Record<ContractStatus, "diag" | "ativa" | "pausada" | "onb" | "alerta"> = {
  draft: "diag",
  active: "ativa",
  paused: "pausada",
  finished: "onb",
  canceled: "alerta",
};

const OPERATOR_LABEL: Record<OperatorType, string> = {
  tributo_devido: "Tributo Devido",
  organization: "Organização",
  workspace: "Workspace",
};

type StatusFilter = "all" | ContractStatus;
type ContractsTab = "contratos" | "templates";

// Teto de listagem do backend (le=200). Acima disso a lista vem cortada.
const CONTRACTS_PAGE_LIMIT = 200;

export function ContractsPage() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const navigate = useNavigate();
  const [tab, setTab] = useState<ContractsTab>("contratos");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [clientFilter, setClientFilter] = useState<number | "all">("all");
  const [creating, setCreating] = useState(false);

  // FE-010: o backend lista contratos com `limit` default 50 e teto `le=200`.
  // Consumimos `meta.total` (via useContractsPaged) para que os KPIs/contadores
  // reflitam o TOTAL real do workspace, não só a página carregada; e exibimos um
  // aviso de truncamento quando o total excede o que foi carregado.
  const { data: paged, isLoading } = useContractsPaged(activeWorkspaceId, {
    limit: CONTRACTS_PAGE_LIMIT,
  });
  const contracts = useMemo(() => paged?.data ?? [], [paged]);
  // `total` real do backend (fallback para o que carregou, se o meta vier ausente).
  const total = paged?.meta?.total ?? contracts.length;
  // Truncada quando o total excede a página carregada — os KPIs usam `total`,
  // mas a tabela só consegue mostrar o que veio nesta página.
  const maybeTruncated = total > contracts.length;
  const { data: clients = [] } = useClients(activeWorkspaceId);

  const clientName = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of clients) map.set(c.id, c.legal_name);
    return map;
  }, [clients]);

  const counts = useMemo(() => {
    const draft = contracts.filter((c) => c.status === "draft").length;
    const active = contracts.filter((c) => c.status === "active").length;
    // `total` vem do backend (meta.total); draft/active são da página carregada.
    return { total, draft, active };
  }, [contracts, total]);

  const filtered = useMemo(
    () =>
      contracts.filter(
        (c) =>
          (statusFilter === "all" || c.status === statusFilter) &&
          (clientFilter === "all" || c.client_id === clientFilter),
      ),
    [contracts, statusFilter, clientFilter],
  );

  if (activeWorkspaceId == null) {
    return (
      <>
        <PageHead title="Contratos" subtitle="Contratos e teses tributárias" />
        <EmptyState icon="file-text" title="Selecione um workspace" />
      </>
    );
  }

  const columns: Column<Contract>[] = [
    {
      id: "client",
      header: "Cliente",
      cell: (c) => (
        <span className="razao">
          {c.client_id == null
            ? "Workspace (TD)"
            : (clientName.get(c.client_id) ?? `Cliente #${c.client_id}`)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (c) => <Pill tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Pill>,
    },
    {
      id: "operator",
      header: "Operador",
      cell: (c) => <span className="td-muted">{OPERATOR_LABEL[c.operator_type]}</span>,
    },
    {
      id: "fee",
      header: "Success fee",
      align: "right",
      cell: (c) => formatPct(c.success_fee_pct),
    },
    {
      id: "vigencia",
      header: "Vigência",
      cell: (c) => (
        <span className="td-faded">
          {formatDate(c.data_inicio)} – {formatDate(c.validade)}
        </span>
      ),
    },
  ];

  const tabs: TabItem<ContractsTab>[] = [
    { id: "contratos", label: "Contratos", count: counts.total },
    { id: "templates", label: "Templates" },
  ];

  return (
    <>
      <PageHead
        title="Contratos"
        subtitle={`${counts.total} contratos no workspace`}
        actions={
          tab === "contratos" ? (
            <Guard permission="contracts.write">
              <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
                Novo contrato
              </Button>
            </Guard>
          ) : undefined
        }
      />

      <Tabs items={tabs} active={tab} onChange={setTab} />

      {tab === "templates" ? (
        <ContractTemplatesPanel workspaceId={activeWorkspaceId} />
      ) : (
        <>
      <div className="kpi-grid kpi-grid-3">
        <Kpi
          icon="file-text"
          tone="featured"
          label="Contratos"
          value={String(counts.total)}
          sub="no workspace"
        />
        <Kpi icon="clock" label="Rascunhos" value={String(counts.draft)} sub="aguardando ativação" />
        <Kpi icon="shield-check" label="Ativos" value={String(counts.active)} sub="em vigência" />
      </div>

      <div className="emp-toolbar">
        <Select<ContractStatus>
          variant="chip"
          label="Status"
          value={statusFilter === "all" ? null : statusFilter}
          onChange={(v) => setStatusFilter(v ? v : "all")}
          options={(Object.keys(STATUS_LABEL) as ContractStatus[]).map((s) => ({
            value: s,
            label: STATUS_LABEL[s],
          }))}
          placeholder="Todos"
          clearable
          clearLabel="Todos os status"
        />
        <Select
          variant="chip"
          label="Cliente"
          value={clientFilter === "all" ? null : String(clientFilter)}
          onChange={(v) => setClientFilter(v === "" ? "all" : Number(v))}
          options={clients.map((c) => ({ value: String(c.id), label: c.legal_name }))}
          placeholder="Todos"
          clearable
          clearLabel="Todos os clientes"
        />
        <span className="emp-toolbar-counter">
          {filtered.length} de {counts.total}
        </span>
      </div>

      {maybeTruncated && (
        <div className="onb-submit-error" role="status">
          Mostrando {contracts.length} de {total} contratos. A lista está truncada no
          teto de {CONTRACTS_PAGE_LIMIT} — refine os filtros (status/cliente) para ver os demais.
        </div>
      )}

      <Table
        rows={filtered}
        columns={columns}
        getKey={(c) => String(c.id)}
        onRowClick={(c) => navigate(`/contratos/${c.id}`)}
        empty={
          <EmptyState
            icon="file-text"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhum contrato"}
            description={isLoading ? undefined : "Crie o primeiro contrato deste workspace."}
          />
        }
      />

      {creating && (
        <ContractWizard workspaceId={activeWorkspaceId} onClose={() => setCreating(false)} />
      )}
        </>
      )}
    </>
  );
}
